'use client';

import { c as _c } from "react/compiler-runtime";
import { Search } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { PropsWithChildren, useEffect, useRef } from 'react';
import { useMediaQuery } from 'usehooks-ts';
import WorldSidebarContent from '@/app/[locale]/(worlds)/components/WorldSidebarContent';
import IconGamepad from '@/components/assets/icon_gamepad.svg';
import IconVideo from '@/components/assets/icon_video.svg';
import { useIsMobile } from '@/hooks/useIsMobile';
import { usePathname } from '@/i18n/navigation';
import { getBasePathFromPathname } from '@/lib/url';
import IconExplore from '@/public/images/worlds/icon_explore.svg';
import IconExploreActive from '@/public/images/worlds/icon_explore_active.svg';
import IconFavorite from '@/public/images/worlds/icon_favorite.svg';
import IconGenre from '@/public/images/worlds/icon_genre.svg';
import IconHistory from '@/public/images/worlds/icon_history.svg';
import IconThunder from '@/public/images/worlds/icon_thunder.svg';
import IconUsers from '@/public/images/worlds/icon_users.svg';
import { NavItemType } from '@/types/nav';
import { Bookmark as IconBookmark } from 'lucide-react';
type WorldsLayoutProps = PropsWithChildren<{
  isSignedIn?: boolean;
}>;
export default function WorldsLayout(t0) {
  const $ = _c(142);
  const {
    children,
    isSignedIn
  } = t0;
  const t = useTranslations();
  const pathname = usePathname();
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {};
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const itemRefs = useRef(t1);
  const basePath = getBasePathFromPathname(pathname);
  let t2;
  if ($[1] !== t) {
    t2 = t("worlds-navigation.nav.liked");
    $[1] = t;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== t2) {
    t3 = {
      id: "liked",
      label: t2,
      Icon: IconFavorite,
      path: "/liked"
    };
    $[3] = t2;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t) {
    t4 = t("worlds-navigation.nav.saved");
    $[5] = t;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t4) {
    t5 = {
      id: "saved",
      label: t4,
      Icon: IconBookmark,
      isNativeIcon: true,
      iconGradientColor: "stroke" as const,
      path: "/saved"
    };
    $[7] = t4;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t3 || $[10] !== t5) {
    t6 = [t3, t5];
    $[9] = t3;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const authenticatedNavItems = t6;
  let t7;
  if ($[12] !== t) {
    t7 = t("worlds-navigation.nav.home");
    $[12] = t;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== t7) {
    t8 = {
      id: "home",
      label: t7,
      Icon: IconExplore,
      IconActive: IconExploreActive,
      path: "/"
    };
    $[14] = t7;
    $[15] = t8;
  } else {
    t8 = $[15];
  }
  let t9;
  if ($[16] !== t) {
    t9 = t("partner.content-showcase.games.title");
    $[16] = t;
    $[17] = t9;
  } else {
    t9 = $[17];
  }
  let t10;
  if ($[18] !== t9) {
    t10 = {
      id: "games",
      label: t9,
      Icon: IconGamepad,
      path: "/games"
    };
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  let t11;
  if ($[20] !== t) {
    t11 = t("worlds-navigation.nav.videos");
    $[20] = t;
    $[21] = t11;
  } else {
    t11 = $[21];
  }
  let t12;
  if ($[22] !== t11) {
    t12 = {
      id: "videos",
      label: t11,
      Icon: IconVideo,
      path: "/videos"
    };
    $[22] = t11;
    $[23] = t12;
  } else {
    t12 = $[23];
  }
  let t13;
  if ($[24] !== t) {
    t13 = t("worlds-navigation.nav.creators");
    $[24] = t;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  let t14;
  if ($[26] !== t13) {
    t14 = {
      id: "creators",
      label: t13,
      Icon: IconUsers,
      path: "/creators"
    };
    $[26] = t13;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== authenticatedNavItems || $[29] !== isSignedIn) {
    t15 = isSignedIn ? authenticatedNavItems : [];
    $[28] = authenticatedNavItems;
    $[29] = isSignedIn;
    $[30] = t15;
  } else {
    t15 = $[30];
  }
  let t16;
  if ($[31] !== t) {
    t16 = t("worlds-navigation.nav.history");
    $[31] = t;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  let t17;
  if ($[33] !== t16) {
    t17 = {
      id: "history",
      label: t16,
      Icon: IconHistory,
      path: "/history"
    };
    $[33] = t16;
    $[34] = t17;
  } else {
    t17 = $[34];
  }
  let t18;
  if ($[35] !== t) {
    t18 = t("worlds-navigation.nav.new");
    $[35] = t;
    $[36] = t18;
  } else {
    t18 = $[36];
  }
  let t19;
  if ($[37] !== t18) {
    t19 = {
      id: "new",
      label: t18,
      Icon: IconThunder,
      path: "/new"
    };
    $[37] = t18;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  let t20;
  if ($[39] !== t) {
    t20 = t("worlds-navigation.nav.genre");
    $[39] = t;
    $[40] = t20;
  } else {
    t20 = $[40];
  }
  let t21;
  if ($[41] !== t20) {
    t21 = {
      id: "genre",
      label: t20,
      Icon: IconGenre,
      path: "/genre"
    };
    $[41] = t20;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] !== t10 || $[44] !== t12 || $[45] !== t14 || $[46] !== t15 || $[47] !== t17 || $[48] !== t19 || $[49] !== t21 || $[50] !== t8) {
    t22 = [t8, t10, t12, t14, ...t15, t17, t19, t21];
    $[43] = t10;
    $[44] = t12;
    $[45] = t14;
    $[46] = t15;
    $[47] = t17;
    $[48] = t19;
    $[49] = t21;
    $[50] = t8;
    $[51] = t22;
  } else {
    t22 = $[51];
  }
  const navItems = t22;
  let t23;
  if ($[52] !== t) {
    t23 = t("worlds-navigation.nav.all");
    $[52] = t;
    $[53] = t23;
  } else {
    t23 = $[53];
  }
  let t24;
  if ($[54] !== t23) {
    t24 = {
      id: "home",
      label: t23,
      Icon: IconExplore,
      path: "/"
    };
    $[54] = t23;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  let t25;
  if ($[56] !== t) {
    t25 = t("partner.content-showcase.games.title");
    $[56] = t;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  let t26;
  if ($[58] !== t25) {
    t26 = {
      id: "games",
      label: t25,
      Icon: IconGamepad,
      path: "/games"
    };
    $[58] = t25;
    $[59] = t26;
  } else {
    t26 = $[59];
  }
  let t27;
  if ($[60] !== t) {
    t27 = t("worlds-navigation.nav.videos");
    $[60] = t;
    $[61] = t27;
  } else {
    t27 = $[61];
  }
  let t28;
  if ($[62] !== t27) {
    t28 = {
      id: "videos",
      label: t27,
      Icon: IconVideo,
      path: "/videos"
    };
    $[62] = t27;
    $[63] = t28;
  } else {
    t28 = $[63];
  }
  let t29;
  if ($[64] !== t) {
    t29 = t("worlds-navigation.nav.new");
    $[64] = t;
    $[65] = t29;
  } else {
    t29 = $[65];
  }
  let t30;
  if ($[66] !== t29) {
    t30 = {
      id: "new",
      label: t29,
      Icon: IconThunder,
      path: "/new"
    };
    $[66] = t29;
    $[67] = t30;
  } else {
    t30 = $[67];
  }
  let t31;
  if ($[68] !== isSignedIn || $[69] !== t) {
    t31 = isSignedIn ? [{
      id: "liked",
      label: t("worlds-navigation.nav.liked"),
      Icon: IconFavorite,
      path: "/liked"
    }, {
      id: "saved",
      label: t("worlds-navigation.nav.saved"),
      Icon: IconBookmark,
      path: "/saved"
    }] : [];
    $[68] = isSignedIn;
    $[69] = t;
    $[70] = t31;
  } else {
    t31 = $[70];
  }
  let t32;
  if ($[71] !== t) {
    t32 = t("worlds-navigation.nav.genre");
    $[71] = t;
    $[72] = t32;
  } else {
    t32 = $[72];
  }
  let t33;
  if ($[73] !== t32) {
    t33 = {
      id: "genre",
      label: t32,
      Icon: IconGenre,
      path: "/genre"
    };
    $[73] = t32;
    $[74] = t33;
  } else {
    t33 = $[74];
  }
  let t34;
  if ($[75] !== t24 || $[76] !== t26 || $[77] !== t28 || $[78] !== t30 || $[79] !== t31 || $[80] !== t33) {
    t34 = [t24, t26, t28, t30, ...t31, t33];
    $[75] = t24;
    $[76] = t26;
    $[77] = t28;
    $[78] = t30;
    $[79] = t31;
    $[80] = t33;
    $[81] = t34;
  } else {
    t34 = $[81];
  }
  const navItemsApp = t34;
  let t35;
  if ($[82] !== t) {
    t35 = t("worlds-navigation.nav.home");
    $[82] = t;
    $[83] = t35;
  } else {
    t35 = $[83];
  }
  let t36;
  if ($[84] !== t35) {
    t36 = {
      id: "home",
      label: t35,
      Icon: IconExplore,
      IconActive: IconExploreActive,
      path: "/"
    };
    $[84] = t35;
    $[85] = t36;
  } else {
    t36 = $[85];
  }
  let t37;
  if ($[86] !== t) {
    t37 = t("worlds-navigation.nav.search");
    $[86] = t;
    $[87] = t37;
  } else {
    t37 = $[87];
  }
  let t38;
  if ($[88] !== t37) {
    t38 = {
      id: "search",
      label: t37,
      Icon: Search,
      isNativeIcon: true,
      iconGradientColor: "stroke",
      path: "/search"
    };
    $[88] = t37;
    $[89] = t38;
  } else {
    t38 = $[89];
  }
  let t39;
  if ($[90] !== t) {
    t39 = t("partner.content-showcase.games.title");
    $[90] = t;
    $[91] = t39;
  } else {
    t39 = $[91];
  }
  let t40;
  if ($[92] !== t39) {
    t40 = {
      id: "games",
      label: t39,
      Icon: IconGamepad,
      path: "/games"
    };
    $[92] = t39;
    $[93] = t40;
  } else {
    t40 = $[93];
  }
  let t41;
  if ($[94] !== t) {
    t41 = t("worlds-navigation.nav.creators");
    $[94] = t;
    $[95] = t41;
  } else {
    t41 = $[95];
  }
  let t42;
  if ($[96] !== t41) {
    t42 = {
      id: "creators",
      label: t41,
      Icon: IconUsers,
      path: "/creators"
    };
    $[96] = t41;
    $[97] = t42;
  } else {
    t42 = $[97];
  }
  let t43;
  if ($[98] !== t) {
    t43 = t("worlds-navigation.nav.more");
    $[98] = t;
    $[99] = t43;
  } else {
    t43 = $[99];
  }
  let t44;
  if ($[100] !== t43) {
    t44 = {
      id: "more",
      label: t43,
      Icon: IconGenre
    };
    $[100] = t43;
    $[101] = t44;
  } else {
    t44 = $[101];
  }
  let t45;
  if ($[102] !== t) {
    t45 = t("worlds-navigation.nav.videos");
    $[102] = t;
    $[103] = t45;
  } else {
    t45 = $[103];
  }
  let t46;
  if ($[104] !== t45) {
    t46 = {
      id: "videos",
      label: t45,
      Icon: IconVideo,
      path: "/videos"
    };
    $[104] = t45;
    $[105] = t46;
  } else {
    t46 = $[105];
  }
  let t47;
  if ($[106] !== authenticatedNavItems || $[107] !== isSignedIn) {
    t47 = isSignedIn ? authenticatedNavItems : [];
    $[106] = authenticatedNavItems;
    $[107] = isSignedIn;
    $[108] = t47;
  } else {
    t47 = $[108];
  }
  let t48;
  if ($[109] !== t) {
    t48 = t("worlds-navigation.nav.history");
    $[109] = t;
    $[110] = t48;
  } else {
    t48 = $[110];
  }
  let t49;
  if ($[111] !== t48) {
    t49 = {
      id: "history",
      label: t48,
      Icon: IconHistory,
      path: "/history"
    };
    $[111] = t48;
    $[112] = t49;
  } else {
    t49 = $[112];
  }
  let t50;
  if ($[113] !== t) {
    t50 = t("worlds-navigation.nav.new");
    $[113] = t;
    $[114] = t50;
  } else {
    t50 = $[114];
  }
  let t51;
  if ($[115] !== t50) {
    t51 = {
      id: "new",
      label: t50,
      Icon: IconThunder,
      path: "/new"
    };
    $[115] = t50;
    $[116] = t51;
  } else {
    t51 = $[116];
  }
  let t52;
  if ($[117] !== t) {
    t52 = t("worlds-navigation.nav.genre");
    $[117] = t;
    $[118] = t52;
  } else {
    t52 = $[118];
  }
  let t53;
  if ($[119] !== t52) {
    t53 = {
      id: "genre",
      label: t52,
      Icon: IconGenre,
      path: "/genre"
    };
    $[119] = t52;
    $[120] = t53;
  } else {
    t53 = $[120];
  }
  let t54;
  if ($[121] !== t36 || $[122] !== t38 || $[123] !== t40 || $[124] !== t42 || $[125] !== t44 || $[126] !== t46 || $[127] !== t47 || $[128] !== t49 || $[129] !== t51 || $[130] !== t53) {
    t54 = [t36, t38, t40, t42, t44, t46, ...t47, t49, t51, t53];
    $[121] = t36;
    $[122] = t38;
    $[123] = t40;
    $[124] = t42;
    $[125] = t44;
    $[126] = t46;
    $[127] = t47;
    $[128] = t49;
    $[129] = t51;
    $[130] = t53;
    $[131] = t54;
  } else {
    t54 = $[131];
  }
  const navItemsMobile = t54;
  const isMobile = useIsMobile();
  const activeItem = isMobile ? navItemsMobile.find(item => item.path === basePath)?.id || "home" : navItems.find(item_0 => item_0.path === basePath)?.id || "home";
  let t55;
  let t56;
  if ($[132] !== activeItem || $[133] !== isDesktop) {
    t55 = () => {
      if (isDesktop) {
        return;
      }
      const el = itemRefs.current[activeItem];
      if (el) {
        el.scrollIntoView({
          behavior: "smooth",
          inline: "center",
          block: "nearest"
        });
        el.focus({
          preventScroll: true
        });
      }
    };
    t56 = [activeItem, isDesktop];
    $[132] = activeItem;
    $[133] = isDesktop;
    $[134] = t55;
    $[135] = t56;
  } else {
    t55 = $[134];
    t56 = $[135];
  }
  useEffect(t55, t56);
  let t57;
  if ($[136] !== activeItem || $[137] !== children || $[138] !== navItems || $[139] !== navItemsApp || $[140] !== navItemsMobile) {
    t57 = <WorldSidebarContent navItems={navItems} navItemsMobile={navItemsMobile} navItemsApp={navItemsApp} activeItem={activeItem} itemRefs={itemRefs}>{children}</WorldSidebarContent>;
    $[136] = activeItem;
    $[137] = children;
    $[138] = navItems;
    $[139] = navItemsApp;
    $[140] = navItemsMobile;
    $[141] = t57;
  } else {
    t57 = $[141];
  }
  return t57;
}